import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  alertsApi,
  appSettingsApi,
  dashboardApi,
  dossiersApi,
  gscApi,
  integrationsApi,
  notificationsApi,
  tasksApi,
  healthApi,
  securityApi,
  scansApi,
  seoApi,
  systemApi,
  inventoryApi,
  wordpressApi,
  type DossierCreate,
  type IntegrationCreate,
  type ContentAudit,
  type ScanStatus,
  type ScanTypeSummary,
  type ScanScheduleUpdate,
  type WordPressSiteCredentials,
} from '@/api/client'

// Integration hooks
export function useIntegrations(type?: string) {
  return useQuery({
    queryKey: ['integrations', type],
    queryFn: () => integrationsApi.list(type),
  })
}

export function useIntegrationTypes() {
  return useQuery({
    queryKey: ['integrations', 'types'],
    queryFn: integrationsApi.getTypes,
  })
}

export function useCreateIntegration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: IntegrationCreate) => integrationsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateIntegration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<IntegrationCreate> }) =>
      integrationsApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      queryClient.invalidateQueries({ queryKey: ['integrations', id] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useDeleteIntegration() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => integrationsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integrations'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useTestIntegration() {
  return useMutation({
    mutationFn: (id: number) => integrationsApi.test(id),
  })
}

export function useTriggerHealthCheck() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: tasksApi.triggerHealthCheck,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
}

// Security hooks
export function useSecurityStats() {
  return useQuery({
    queryKey: ['security', 'stats'],
    queryFn: securityApi.getStats,
    refetchInterval: 30000,
  })
}

export function useScanFindings(scanId: number, params?: { severity?: string }) {
  return useQuery({
    queryKey: ['security', 'findings', scanId, params],
    queryFn: () => securityApi.getScanFindings(scanId, params),
    enabled: scanId > 0,
  })
}

export function useStartScan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { target_url: string; scan_type: string }) =>
      securityApi.startScan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security'] })
    },
  })
}

export function useCancelScan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (scanId: number) => securityApi.cancelScan(scanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security'] })
    },
  })
}

export function useMarkFalsePositive() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ findingId, isFalsePositive }: { findingId: number; isFalsePositive: boolean }) =>
      securityApi.markFalsePositive(findingId, isFalsePositive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security'] })
    },
  })
}

// Inventory hooks (SBOM components + CVE's per target)
export function useInventoryOverview() {
  return useQuery({
    queryKey: ['inventory', 'overview'],
    queryFn: inventoryApi.getOverview,
    refetchInterval: 60000,
  })
}

// refresh-intel (POST /inventory/refresh-intel) only dispatches Celery tasks
// and returns immediately — the actual OSV/NVD re-check runs in the
// background over the next "few minutes". Its onSuccess used to invalidate
// these queries right away, which just re-fetched the SAME unchanged data
// (nothing had run yet) and never fetched again — from the user's side,
// clicking "Ververs CVE-data" looked like it silently did nothing. Poll
// while the Inventory page is open so a completed background refresh
// actually shows up without a manual page reload.
export function useInventoryComponents(targetId: number) {
  return useQuery({
    queryKey: ['inventory', 'components', targetId],
    queryFn: () => inventoryApi.getComponents(targetId),
    enabled: targetId > 0,
    refetchInterval: 10000,
  })
}

export function useInventoryVulnerabilities(componentId: number | null) {
  return useQuery({
    queryKey: ['inventory', 'vulnerabilities', componentId],
    queryFn: () => inventoryApi.getVulnerabilities(componentId as number),
    enabled: componentId !== null && componentId > 0,
    refetchInterval: 10000,
  })
}

export function useAllInventoryVulnerabilities(targetId: number) {
  return useQuery({
    queryKey: ['inventory', 'vulnerabilities', 'all', targetId],
    queryFn: () => inventoryApi.getAllVulnerabilities(targetId),
    enabled: targetId > 0,
  })
}

export function useRefreshInventoryIntel() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (targetId: number) => inventoryApi.refreshIntel(targetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] })
    },
  })
}

// Alert hooks
export function useDashboardOverview() {
  return useQuery({
    queryKey: ['dashboard', 'overview'],
    queryFn: dashboardApi.getOverview,
    refetchInterval: 30000,
  })
}

export function useBulkUpdateAlertStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, status }: { ids: number[]; status: string }) =>
      alertsApi.bulkUpdateStatus(ids, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })
}

export function useAlerts(params?: {
  status?: string
  exclude_kind?: string
  target_id?: number
  dossier_id?: number
  kind?: string
  severity?: string
  kev_only?: boolean
  no_dossier?: boolean
  limit?: number
}, enabled = true) {
  return useQuery({
    queryKey: ['alerts', params],
    enabled,
    queryFn: () => alertsApi.list(params),
    refetchInterval: 60000,
  })
}

export function useSetAlertsVex() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      ids,
      status,
      justification,
    }: {
      ids: number[]
      status: 'not_affected' | 'false_positive'
      justification: string
    }) => alertsApi.setVex(ids, status, justification),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })
}

export function useAlertSite(dossierId: number | null) {
  return useQuery({
    queryKey: ['alerts', 'site', dossierId],
    queryFn: () => alertsApi.getSite(dossierId as number),
    enabled: dossierId !== null,
  })
}

export function useAlertSites() {
  return useQuery({
    queryKey: ['alerts', 'sites'],
    queryFn: alertsApi.getSites,
    refetchInterval: 60000,
  })
}

export function useClaudeInstruction(dossierId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['alerts', 'claude-instruction', dossierId],
    queryFn: () => alertsApi.getClaudeInstruction(dossierId as number),
    enabled: enabled && dossierId !== null,
  })
}

/** Starts a recheck and polls it until the evaluation is done or failed. */
export function useRecheckSite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (dossierId: number) => {
      const { task_id } = await alertsApi.recheck(dossierId)
      // A large inventory takes about a minute; give up after five.
      for (let i = 0; i < 150; i++) {
        await new Promise((resolve) => setTimeout(resolve, 2000))
        const status = await alertsApi.recheckStatus(task_id)
        if (status.state === 'done') return status.result
        if (status.state === 'failed') throw new Error(status.error)
      }
      throw new Error('De controle duurt langer dan verwacht; kijk straks opnieuw.')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
      queryClient.invalidateQueries({ queryKey: ['inventory'] })
    },
  })
}

export function useAlertStats() {
  return useQuery({
    queryKey: ['alerts', 'stats'],
    queryFn: alertsApi.getStats,
    refetchInterval: 60000,
  })
}

export function useUpdateAlertStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: 'new' | 'acked' | 'resolved' }) =>
      alertsApi.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })
}

// Health hooks
export function useHealthCheck() {
  return useQuery({
    queryKey: ['health'],
    queryFn: healthApi.check,
    refetchInterval: 60000, // Every minute
  })
}

// Notification hooks (in-app bell)
export function useNotifications(params?: { unread_only?: boolean; limit?: number }) {
  return useQuery({
    queryKey: ['notifications', params],
    queryFn: () => notificationsApi.list(params),
    refetchInterval: 60000,
  })
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 60000,
  })
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => notificationsApi.markRead(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  })
}

export function useSeoFindings(
  scanId: number,
  params?: { category?: string; severity?: string },
) {
  return useQuery({
    queryKey: ['seo', 'findings', scanId, params],
    queryFn: () => seoApi.getScanFindings(scanId, params),
    enabled: scanId > 0,
  })
}

export function useStartSeoScan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      target_url: string
      scan_type: string
      max_pages?: number
      dossier_id?: number
      render_js?: boolean
    }) => seoApi.startScan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo'] })
    },
  })
}

export function useCancelSeoScan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (scanId: number) => seoApi.cancelScan(scanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo'] })
    },
  })
}

export function useDeleteSeoScan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (scanId: number) => seoApi.deleteScan(scanId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo'] })
      // The unified history on /scans reads its own key.
      queryClient.invalidateQueries({ queryKey: ['scans'] })
    },
  })
}

// App Settings hooks
export function useAppSettings() {
  return useQuery({
    queryKey: ['app-settings'],
    queryFn: appSettingsApi.list,
  })
}

export function useUpdateAppSetting() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string | null }) =>
      appSettingsApi.update(key, value),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['app-settings'] })
      // Invalidate GSC status immediately when a GSC setting changes so the
      // "Verbind Google Account" button appears without needing a page reload.
      if (variables.key.startsWith('gsc_')) {
        queryClient.invalidateQueries({ queryKey: ['gsc', 'status'] })
      }
    },
  })
}

// Dossier hooks
export function useDossiers(params?: { active?: boolean }) {
  return useQuery({
    queryKey: ['dossiers', params],
    queryFn: () => dossiersApi.list(params),
  })
}

export function useDossier(id: number) {
  return useQuery({
    queryKey: ['dossiers', id],
    queryFn: () => dossiersApi.get(id),
    enabled: id > 0,
  })
}

export function useDossierSnapshots(id: number, days = 90) {
  return useQuery({
    queryKey: ['dossiers', id, 'snapshots', days],
    queryFn: () => dossiersApi.getSnapshots(id, days),
    enabled: id > 0,
  })
}

export function useDossierScans(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'scans'],
    queryFn: () => dossiersApi.getScans(id),
    enabled: id > 0,
    refetchInterval: (query) => {
      const scans = query.state.data
      return scans?.some(s => s.status === 'pending' || s.status === 'running') ? 5000 : false
    },
  })
}

export function useDossierScoreHistory(id: number, days = 90) {
  return useQuery({
    queryKey: ['dossiers', id, 'score-history', days],
    queryFn: () => dossiersApi.getScoreHistory(id, days),
    enabled: id > 0,
  })
}

export function useDossierIndexReport(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'index-report'],
    queryFn: () => dossiersApi.getIndexReport(id),
    enabled: id > 0,
  })
}

export function useDossierPositionBuckets(id: number, days = 28) {
  return useQuery({
    queryKey: ['dossiers', id, 'position-buckets', days],
    queryFn: () => dossiersApi.getPositionBuckets(id, days),
    enabled: id > 0,
  })
}

export function useDossierGscTrends(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'gsc-trends'],
    queryFn: () => dossiersApi.getGscTrends(id),
    enabled: id > 0,
    retry: 1,
  })
}

export function useDossierCompetitorComparison(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'competitor-comparison'],
    queryFn: () => dossiersApi.getCompetitorComparison(id),
    enabled: id > 0,
  })
}

export function useScanCompetitors() {
  return useMutation({
    mutationFn: (id: number) => dossiersApi.scanCompetitors(id),
  })
}

export function useBatchScan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ dossierIds, scanTypes }: { dossierIds: number[]; scanTypes: string[] }) =>
      dossiersApi.batchScan(dossierIds, scanTypes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dossiers', 'active-scans'] })
    },
  })
}

export function useActiveScans() {
  return useQuery({
    queryKey: ['dossiers', 'active-scans'],
    queryFn: () => dossiersApi.getActiveScans(),
    // Live view: poll fast while scans are in flight, slow when idle.
    refetchInterval: (query) => (query.state.data && query.state.data.length > 0 ? 3000 : 8000),
  })
}

export function useDossiersLatestScores() {
  return useQuery({
    queryKey: ['dossiers', 'latest-scores'],
    queryFn: () => dossiersApi.getLatestScores(),
  })
}

export function useRefreshIntel() {
  return useMutation({
    mutationFn: () => securityApi.refreshIntel(),
  })
}

export function useCreateDossier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: DossierCreate) => dossiersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dossiers'] })
    },
  })
}

export function useUpdateDossier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<DossierCreate> & { active?: boolean; skip_scanning?: boolean } }) =>
      dossiersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dossiers'] })
    },
  })
}

export function useGenerateSbomToken() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => dossiersApi.generateSbomToken(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dossiers'] })
      queryClient.invalidateQueries({ queryKey: ['inventory', 'overview'] })
    },
  })
}

export function useDossierSecurityScans(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'security-scans'],
    queryFn: () => dossiersApi.getSecurityScans(id),
    enabled: id > 0,
    refetchInterval: (query) => {
      const scans = query.state.data
      return scans?.some(s => s.status === 'pending' || s.status === 'running') ? 5000 : false
    },
  })
}

export function useDeleteDossier() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => dossiersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dossiers'] })
    },
  })
}

// Google Search Console hooks
export function useGscStatus() {
  return useQuery({
    queryKey: ['gsc', 'status'],
    queryFn: gscApi.status,
    staleTime: 60000,
  })
}

export function useGscProperties() {
  return useQuery({
    queryKey: ['gsc', 'properties'],
    queryFn: gscApi.listProperties,
    staleTime: 300000,
  })
}

export function useGscSitemaps(siteUrl: string) {
  return useQuery({
    queryKey: ['gsc', 'sitemaps', siteUrl],
    queryFn: () => gscApi.getSitemaps(siteUrl),
    enabled: Boolean(siteUrl),
    staleTime: 300000,
  })
}

export function useGscInspect() {
  return useMutation({
    mutationFn: ({ siteUrl, inspectionUrl }: { siteUrl: string; inspectionUrl: string }) =>
      gscApi.inspectUrl(siteUrl, inspectionUrl),
  })
}

export function useScanSchedules(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'scan-schedules'],
    queryFn: () => dossiersApi.getScanSchedules(id),
    enabled: id > 0,
    staleTime: 1000 * 60 * 5,
  })
}

export function useUpdateScanSchedules(dossierId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (schedules: ScanScheduleUpdate[]) =>
      dossiersApi.updateScanSchedules(dossierId, schedules),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dossiers', dossierId, 'scan-schedules'] }),
  })
}

export function useScanSummary(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'scan-summary'],
    queryFn: () => dossiersApi.getScanSummary(id),
    enabled: id > 0,
    refetchInterval: (query) => {
      const items = query.state.data as ScanTypeSummary[] | undefined
      const hasActive = items?.some(
        (s) => s.latest?.status === 'pending' || s.latest?.status === 'running',
      )
      return hasActive ? 5000 : false
    },
  })
}

// Content audit hooks

export function useContentAudits(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'content-audits'],
    queryFn: () => dossiersApi.getContentAudits(id),
    enabled: id > 0,
    refetchInterval: (query) => {
      const audits = query.state.data as ContentAudit[] | undefined
      const hasPending = audits?.some((a) => a.status === 'pending' || a.status === 'running')
      return hasPending ? 4000 : false
    },
  })
}

export function useTriggerContentAudit(dossierId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pageUrl: string) => dossiersApi.triggerContentAudit(dossierId, pageUrl),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dossiers', dossierId, 'content-audits'] })
    },
  })
}

export function useTriggerContentAuditBulk(dossierId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => dossiersApi.triggerContentAuditBulk(dossierId),
    onSuccess: () => {
      // Polling in useContentAudits picks up each audit as it completes.
      qc.invalidateQueries({ queryKey: ['dossiers', dossierId, 'content-audits'] })
    },
  })
}

export function useTopicalGaps(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'topical-gaps'],
    queryFn: () => dossiersApi.getTopicalGaps(id),
    enabled: id > 0,
    staleTime: 1000 * 60 * 30,
  })
}

export function useGscPages(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'gsc-pages'],
    queryFn: () => dossiersApi.getGscPages(id),
    enabled: id > 0,
    staleTime: 1000 * 60 * 60,
  })
}

export function useContentGaps(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'content-gaps'],
    queryFn: () => dossiersApi.getContentGaps(id),
    enabled: id > 0,
    staleTime: 1000 * 60 * 60,
  })
}

export function useDossierSerpAio(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'serp-aio'],
    queryFn: () => dossiersApi.getSerpAio(id),
    enabled: id > 0,
    staleTime: 1000 * 60 * 30,
  })
}

export function useCitationPrompts(id: number) {
  return useQuery({
    queryKey: ['dossiers', id, 'citation-prompts'],
    queryFn: () => dossiersApi.getCitationPrompts(id),
    enabled: id > 0,
  })
}

export function useGenerateCitationPrompts(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => dossiersApi.generateCitationPrompts(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dossiers', id, 'citation-prompts'] }),
  })
}

export function useCreateCitationPrompt(id: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ prompt_text, intent_type }: { prompt_text: string; intent_type?: string }) =>
      dossiersApi.createCitationPrompt(id, prompt_text, intent_type),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dossiers', id, 'citation-prompts'] }),
  })
}

export function useUpdateCitationPrompt(dossierId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ promptId, data }: { promptId: number; data: { active?: boolean; prompt_text?: string } }) =>
      dossiersApi.updateCitationPrompt(dossierId, promptId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dossiers', dossierId, 'citation-prompts'] }),
  })
}

export function useDeleteCitationPrompt(dossierId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (promptId: number) => dossiersApi.deleteCitationPrompt(dossierId, promptId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dossiers', dossierId, 'citation-prompts'] }),
  })
}

// WordPress hooks
export function useWordPressSite(dossierId: number) {
  return useQuery({
    queryKey: ['dossiers', dossierId, 'wordpress'],
    queryFn: () => wordpressApi.get(dossierId),
    enabled: dossierId > 0,
    staleTime: 1000 * 60 * 5,
  })
}

export function useSaveWordPressSite(dossierId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: WordPressSiteCredentials) => wordpressApi.save(dossierId, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dossiers', dossierId, 'wordpress'] })
    },
  })
}

export function useDeleteWordPressSite(dossierId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => wordpressApi.remove(dossierId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dossiers', dossierId, 'wordpress'] })
      qc.invalidateQueries({ queryKey: ['dossiers', dossierId, 'wordpress-scan'] })
    },
  })
}

export function useTestWordPressConnection(dossierId: number) {
  return useMutation({
    mutationFn: (payload: WordPressSiteCredentials) => wordpressApi.test(dossierId, payload),
  })
}

export function useWordPressScanResults(dossierId: number) {
  return useQuery({
    queryKey: ['dossiers', dossierId, 'wordpress-scan'],
    queryFn: () => wordpressApi.getScanResults(dossierId),
    enabled: dossierId > 0,
    staleTime: 1000 * 60 * 10,
  })
}

export function useTriggerWordPressScan(dossierId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => wordpressApi.triggerScan(dossierId),
    onSuccess: () => {
      setTimeout(
        () => qc.invalidateQueries({ queryKey: ['dossiers', dossierId, 'wordpress-scan'] }),
        3000
      )
    },
  })
}

// ── Scans (unified SEO + security history) ───────────────────────────────────

export function useScans(params?: {
  kind?: 'seo' | 'security'
  status?: ScanStatus
  dossier_id?: number
  limit?: number
}) {
  return useQuery({
    queryKey: ['scans', params],
    queryFn: () => scansApi.list(params),
    refetchInterval: 10000,
  })
}

export function useScanHistoryStats(dossierId?: number) {
  return useQuery({
    queryKey: ['scans', 'stats', dossierId],
    queryFn: () => scansApi.stats(dossierId),
    refetchInterval: 30000,
  })
}

// ── System ───────────────────────────────────────────────────────────────────

export function useSystemOverview() {
  return useQuery({
    queryKey: ['system', 'overview'],
    queryFn: systemApi.overview,
    refetchInterval: 30000,
  })
}

export function useMaintenanceLog(limit = 50) {
  return useQuery({
    queryKey: ['system', 'maintenance', limit],
    queryFn: () => systemApi.maintenance(limit),
  })
}
